<script setup>
import { computed, ref, watch } from 'vue';
import Icon from 'next/icon/Icon.vue';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import { useMessageContext } from '../provider.js';

import GalleryView from 'dashboard/components/widgets/conversation/components/GalleryView.vue';

const props = defineProps({
  attachment: {
    type: Object,
    required: true,
  },
});

const MAX_LOAD_RETRIES = 4;
const RETRY_DELAY_MS = 1200;

const hasError = ref(false);
const showGallery = ref(false);
const loadRetryCount = ref(0);
const sourceReloadKey = ref(0);

const { filteredCurrentChatAttachments } = useMessageContext();

const imageUrl = computed(() => {
  const url = props.attachment.dataUrl;
  if (!url) return '';

  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}image_retry=${sourceReloadKey.value}`;
});

const handleError = () => {
  if (loadRetryCount.value < MAX_LOAD_RETRIES) {
    loadRetryCount.value += 1;
    setTimeout(() => {
      sourceReloadKey.value += 1;
    }, RETRY_DELAY_MS * loadRetryCount.value);
    return;
  }

  hasError.value = true;
};

const handleLoad = () => {
  loadRetryCount.value = 0;
  hasError.value = false;
};

watch(
  () => props.attachment.dataUrl,
  () => {
    loadRetryCount.value = 0;
    sourceReloadKey.value = 0;
    hasError.value = false;
  }
);
</script>

<template>
  <div
    class="size-[72px] overflow-hidden contain-content rounded-xl cursor-pointer"
    @click="showGallery = true"
  >
    <div
      v-if="hasError"
      class="flex flex-col items-center justify-center gap-1 text-xs text-center rounded-lg size-full bg-n-alpha-1 text-n-slate-11"
    >
      <Icon icon="i-lucide-circle-off" class="text-n-slate-11" />
      {{ $t('COMPONENTS.MEDIA.LOADING_FAILED') }}
    </div>
    <img
      v-else
      :key="sourceReloadKey"
      class="object-cover w-full h-full skip-context-menu"
      :src="imageUrl"
      @error="handleError"
      @load="handleLoad"
    />
  </div>
  <GalleryView
    v-if="showGallery"
    v-model:show="showGallery"
    :attachment="useSnakeCase(attachment)"
    :all-attachments="filteredCurrentChatAttachments"
    @error="handleError"
    @close="() => (showGallery = false)"
  />
</template>
